import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ship } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

const mockupVariants = cva(
  "flex relative z-10 overflow-hidden shadow-2xl border border-border/5 border-t-border/15",
  {
    variants: {
      type: {
        mobile: "rounded-[48px] max-w-[350px]",
        responsive: "rounded-md",
      },
    },
    defaultVariants: {
      type: "responsive",
    },
  }
);

interface MockupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mockupVariants> {}

const Mockup = React.forwardRef<HTMLDivElement, MockupProps>(
  ({ className, type, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(mockupVariants({ type, className }))}
      {...props}
    />
  )
);
Mockup.displayName = "Mockup";

const glowVariants = cva("absolute w-full", {
  variants: {
    variant: {
      top: "top-0",
      above: "-top-[128px]",
      bottom: "bottom-0",
      below: "-bottom-[128px]",
      center: "top-[50%]",
    },
  },
  defaultVariants: {
    variant: "top",
  },
});

const Glow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof glowVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(glowVariants({ variant }), className)}
    {...props}
  >
    <div
      className={cn(
        "absolute left-1/2 h-[256px] w-[60%] -translate-x-1/2 scale-[2.5] rounded-[50%] bg-[radial-gradient(ellipse_at_center,_hsla(var(--primary-foreground)/.5)_10%,_hsla(var(--primary-foreground)/0)_60%)] sm:h-[512px]",
        variant === "center" && "-translate-y-1/2"
      )}
    />
    <div
      className={cn(
        "absolute left-1/2 h-[128px] w-[40%] -translate-x-1/2 scale-[2] rounded-[50%] bg-[radial-gradient(ellipse_at_center,_hsla(var(--primary)/.3)_10%,_hsla(var(--primary-foreground)/0)_60%)] sm:h-[256px]",
        variant === "center" && "-translate-y-1/2"
      )}
    />
  </div>
));
Glow.displayName = "Glow";

interface HeroProps {
  title: string;
  description: string;
  primaryCta: {
    text: string;
    href: string;
  };
  secondaryCta?: {
    text: string;
    href: string;
  };
  badge?: {
    text: string;
    action?: {
      text: string;
      href: string;
    };
  };
  productImage: {
    src: string;
    alt: string;
  };
  className?: string;
}

function MarineHero({
  title = "Marine Fuel Boost | Premium Marine Fuel Solutions for Optimal Engine Performance",
  description = "FuelOx Marine is a premium fuel additive specifically formulated for boats. Improve fuel efficiency, reduce emissions, and extend engine life with our advanced formula that protects against the harsh marine environment.",
  primaryCta = {
    text: "Shop Now",
    href: "#products",
  },
  secondaryCta = {
    text: "View Benefits",
    href: "/benefits",
  },
  badge = {
    text: "New Formula",
    action: {
      text: "Learn More",
      href: "/about",
    },
  },
  productImage = {
    src: "/images/products/16oz-bottle.png",
    alt: "Bottle of FuelOx Marine premium fuel additive",
  },
  className,
}: Partial<HeroProps>) {
  return (
    <section
      className={cn(
        "relative bg-transparent text-gray-900",
        "py-12 px-4 md:py-24 lg:py-32",
        "overflow-hidden min-h-[600px]",
        className
      )}
    >
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          className="absolute inset-0 w-full h-full object-cover"
          aria-label="Background video showing a speedboat on water"
          preload="auto"
          poster="/images/video-poster.jpg"
        >
          <source 
            src="videos/speedboat.mp4" 
            type="video/mp4"
            media="(min-width: 768px)"
          />
          <source 
            src="videos/speedboat-mobile.mp4" 
            type="video/mp4"
            media="(max-width: 767px)"
          />
          <track 
            kind="descriptions" 
            src="videos/speedboat-descriptions.vtt" 
            label="Video Descriptions"
          />
          <p>Your browser doesn't support HTML5 video. Here's a <a href="videos/speedboat.mp4">link to the video</a> instead.</p>
        </video>
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-white/70" />
      </div>

      <div className="relative z-[1] mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 max-w-3xl">
          {/* Badge */}
          {badge && (
            <Badge
              variant="outline"
              className="animate-appear gap-2 self-start bg-marine/10 text-marine border-marine/20"
            >
              <Ship className="h-3 w-3" aria-hidden="true" />
              <span>{badge.text}</span>
              {badge.action && (
                <a href={badge.action.href} className="flex items-center gap-1">
                  {badge.action.text}
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </a>
              )}
            </Badge>
          )}

          {/* Title */}
          <div className="flex flex-col items-center justify-center text-center lg:text-left lg:items-start lg:py-32 py-24 px-8">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              {title}
            </h1>
          </div>

          {/* Description */}
          <p className="text-lg text-marine-dark max-w-prose relative">
            {description}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mt-2">
            <Button
              size="lg"
              className="bg-marine hover:bg-marine-light text-white relative"
              asChild
            >
              <a href={primaryCta.href}>{primaryCta.text}</a>
            </Button>

            {secondaryCta && (
              <Button
                size="lg"
                variant="outline"
                className="border-marine text-marine hover:bg-marine/10 relative"
                asChild
              >
                <a href={secondaryCta.href}>{secondaryCta.text}</a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Wave decoration */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-20 bg-blue-50 dark:bg-blue-950 -z-10 opacity-50"
        aria-hidden="true"
      >
        <svg
          className="absolute bottom-0 w-full h-20"
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25L1440 53.25V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
            fill="currentColor"
            className="text-background"
          />
        </svg>
      </div>
    </section>
  );
}

function FuelOxMarineHero() {
  return (
    <MarineHero
      title="Marine Fuel Boost | Premium Marine Fuel Solutions for Optimal Engine Performance"
      description="FuelOx Marine is a premium fuel additive specifically formulated for boats. Improve fuel efficiency, reduce emissions, and extend engine life with our advanced formula that protects against the harsh marine environment."
      productImage={{
        src: "/images/products/16oz-bottle.png",
        alt: "Bottle of FuelOx Marine premium fuel additive"
      }}
      primaryCta={{
        text: "Shop Now",
        href: "#products",
      }}
      secondaryCta={{
        text: "View Benefits",
        href: "/benefits",
      }}
      badge={{
        text: "New Formula",
        action: {
          text: "Learn More",
          href: "/about",
        },
      }}
    />
  );
}

interface HeroVideoProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  description?: string;
  ctaText?: string;
  ctaHref?: string;
  videoSrc?: string;
}

const HeroVideo = React.forwardRef<HTMLDivElement, HeroVideoProps>(
  (
    {
      className,
      title = "Maximize Your Marine Engine's Performance",
      subtitle = "Advanced Fuel Treatment",
      description = "The ultimate solution for marine engine protection, enhanced performance, and improved fuel efficiency. Trusted by boat owners worldwide.",
      ctaText = "Shop Now",
      ctaHref = "#products",
      videoSrc = "/videos/speedboat.mp4",
      ...props
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    const fadeUpVariants = {
      hidden: { opacity: 0, y: 30 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
          duration: 1,
          delay: 0.5 + i * 0.2,
          ease: [0.25, 0.4, 0.25, 1],
        },
      }),
    };

    return (
      <div
        ref={ref}
        className={cn(
          "relative min-h-[90vh] md:min-h-screen w-full flex items-center justify-center overflow-hidden bg-background",
          className
        )}
        {...props}
      >
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          >
            <source src="/videos/speedboat.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black/50" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 md:px-6 py-20 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              custom={0}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.08] mb-8 md:mb-12"
            >
              <span className="text-base md:text-lg text-white/80 tracking-wide font-medium">
                {subtitle}
              </span>
            </motion.div>

            <motion.div
              custom={1}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
            >
              <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold mb-6 md:mb-8 tracking-tight leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-white/90">
                  {title}
                </span>
              </h1>
            </motion.div>

            <motion.div
              custom={2}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
            >
              <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-10 leading-relaxed font-light tracking-wide max-w-2xl mx-auto">
                {description}
              </p>
            </motion.div>

            <motion.div
              custom={3}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className="flex justify-center"
            >
              <Button size="lg" className="bg-marine hover:bg-marine-dark text-white px-8 py-6 text-lg" asChild>
                <a href={ctaHref}>
                  {ctaText}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50 pointer-events-none" />
      </div>
    );
  }
);

HeroVideo.displayName = "HeroVideo";

export default HeroVideo;
